import { scaleQuantize } from 'd3-scale'
import { Money } from '../primitives/Money'
import './dayHeatmap.css'

/**
 * A calendar month as a grid of days, Monday first, four token steps. Presentational:
 * it never fetches and does no money arithmetic (the day figures arrive summed).
 *
 * Level scale: L0 is no spend (zero or below, after refunds); L1-L3 quantise the month's
 * positive days over [0, max]. A day after `today` is "still to come": an outline, no level.
 *
 * Each in-month cell carries its meaning as visually hidden text (weekday, date, amount
 * through `Money`, level word), because hover is not the channel and `aria-label` cannot
 * hold a `Money`. Colour is never the only signal (rule 11).
 */

export type HeatmapDay = { date: string; spentCents: number }
type Props = {
  days: readonly HeatmapDay[]
  currency: string
  today: string
  /** Monday = 0; its label is bold. */
  worst: 0 | 1 | 2 | 3 | 4 | 5 | 6 | null
  labelledBy: string
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const
const LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const
const WORDS = ['no spending', 'low', 'medium', 'high'] as const

const parts = (date: string): [number, number, number] => {
  const [y, m, d] = date.split('-').map(Number)
  return [y as number, m as number, d as number]
}
/** Mon = 0, in UTC so no local offset moves it. */
const weekday = (date: string): number => {
  const [y, m, d] = parts(date)
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
}

export function DayHeatmap({ days, currency, today, worst, labelledBy }: Props) {
  const max = Math.max(0, ...days.map((d) => d.spentCents))
  const quantise = scaleQuantize<1 | 2 | 3>().domain([0, Math.max(max, 1)]).range([1, 2, 3])
  const lead = days[0] === undefined ? 0 : weekday(days[0].date)
  const trail = (7 - ((lead + days.length) % 7)) % 7
  return (
    <div className="dh">
      <ul className="dh-grid" role="list" aria-labelledby={labelledBy}>
        {Array.from({ length: lead }, (_, i) => (
          <li key={`lead-${i}`} className="dh-cell dh-cell--blank" aria-hidden="true" role="presentation" />
        ))}
        {days.map((d) => {
          const [, m, day] = parts(d.date)
          const name = `${WEEKDAYS[weekday(d.date)]} ${day} ${MONTHS[m - 1]}`
          if (d.date > today) {
            return (
              <li key={d.date} role="listitem" className="dh-cell dh-cell--future" data-chart>
                <span className="sr-only">{`${name}: still to come`}</span>
              </li>
            )
          }
          const lvl = d.spentCents > 0 ? quantise(d.spentCents) : 0
          return (
            <li key={d.date} role="listitem" className={`dh-cell dh-cell--l${lvl}`} data-chart>
              <span className="sr-only">
                {`${name}: `}
                {d.spentCents > 0 ? <Money cents={d.spentCents} currency={currency} display="whole" kind="stock" /> : null}
                {d.spentCents > 0 ? `, ${WORDS[lvl]}` : WORDS[0]}
              </span>
            </li>
          )
        })}
        {Array.from({ length: trail }, (_, i) => (
          <li key={`trail-${i}`} className="dh-cell dh-cell--blank" aria-hidden="true" role="presentation" />
        ))}
      </ul>
      <div className="dh-weekdays" aria-hidden="true">
        {LETTERS.map((l, i) => (
          <span key={WEEKDAYS[i]} className={worst === i ? 'dh-weekday dh-weekday--worst' : 'dh-weekday'}>
            {l}
          </span>
        ))}
      </div>
      <div className="dh-legend" aria-hidden="true">
        <span className="dh-legend__word">less</span>
        <div className="dh-legend__steps">
          {[0, 1, 2, 3].map((l) => (
            <span key={l} className={`dh-legend__step dh-legend__step--l${l}`} data-chart />
          ))}
        </div>
        <span className="dh-legend__word">more</span>
      </div>
    </div>
  )
}
